import { useEffect, useMemo, useRef, useState } from 'react';
import { useCode, isDirty } from '../../state/code-store';

/**
 * Builds a nested tree from a flat list of "a/b/c.ext" paths so it can be
 * rendered as a collapsible tree (Shopify-ish look, not VSCode).
 */
interface TreeNode {
  name: string;
  path: string;     // full path for files, folder path for dirs
  children: TreeNode[];
  isFile: boolean;
}

function buildTree(files: string[]): TreeNode {
  const root: TreeNode = { name: '', path: '', children: [], isFile: false };
  for (const file of files) {
    const parts = file.split('/');
    let node = root;
    let acc = '';
    for (let i = 0; i < parts.length; i++) {
      const name = parts[i];
      acc = acc ? `${acc}/${name}` : name;
      const isFile = i === parts.length - 1;
      let child = node.children.find((c) => c.name === name);
      if (!child) {
        child = { name, path: acc, children: [], isFile };
        node.children.push(child);
      }
      node = child;
    }
  }
  sortTree(root);
  return root;
}

function sortTree(node: TreeNode) {
  node.children.sort((a, b) => {
    if (a.isFile !== b.isFile) return a.isFile ? 1 : -1; // folders first
    return a.name.localeCompare(b.name);
  });
  for (const c of node.children) if (!c.isFile) sortTree(c);
}

// ---------------------------------------------------------------------------

function FileIcon({ name }: { name: string }) {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  const common = 'h-[14px] w-[14px] shrink-0';
  const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.75, strokeLinecap: 'round', strokeLinejoin: 'round' } as any;
  const color = {
    liquid: 'text-[#6366f1]',
    json: 'text-[#b98900]',
    css: 'text-[#005bd3]',
    scss: 'text-[#005bd3]',
    js: 'text-[#b98900]',
    ts: 'text-[#005bd3]',
    html: 'text-[#d72c0d]',
    svg: 'text-[#8c9196]',
  }[ext] || 'text-icon-subdued';

  return (
    <svg viewBox="0 0 24 24" className={`${common} ${color}`} {...stroke}>
      <path d="M14 3v4a1 1 0 0 0 1 1h4" />
      <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
    </svg>
  );
}

function FolderIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="h-[14px] w-[14px] shrink-0 text-icon-subdued"
      fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      {open ? (
        <path d="m4 11 2 7h12l2-7M4 11V7a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v2" />
      ) : (
        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      )}
    </svg>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="10" height="10" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      className={'shrink-0 text-icon-subdued transition-transform ' + (open ? 'rotate-90' : '')}
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

// ---------------------------------------------------------------------------

/**
 * Payload do context menu — coordenadas + o nó alvo (arquivo, pasta, ou
 * root vazio pra ações "sem alvo específico"). Ampliado pra suportar
 * conjunto ~igual ao VS Code (Novo, Duplicar, Renomear, Copiar caminho,
 * Copiar caminho relativo, Download, Excluir, Upload).
 */
interface CtxTarget {
  x: number;
  y: number;
  node: TreeNode | null;   // null = fundo da árvore (root)
}

interface CtxSetter {
  open: (target: CtxTarget) => void;
}

// ---------------------------------------------------------------------------

function NodeRow({
  node,
  depth,
  defaultOpen,
  ctx,
}: {
  node: TreeNode;
  depth: number;
  defaultOpen: boolean;
  ctx: CtxSetter;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const openFile = useCode((s) => s.openFile);
  const activePath = useCode((s) => s.activePath);
  const tab = useCode((s) => (node.isFile ? s.tabs[node.path] : undefined));
  const customized = useCode((s) => (node.isFile ? s.customized.has(node.path) : false));
  const dirty = node.isFile && isDirty(tab);
  const selected = node.isFile && activePath === node.path;

  const pad = { paddingLeft: 4 + depth * 12 };

  function onCtx(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    ctx.open({ x: e.clientX, y: e.clientY, node });
  }

  if (node.isFile) {
    return (
      <button
        style={pad}
        data-selected={selected ? 'true' : 'false'}
        className="lc-row w-full justify-start gap-1.5"
        onClick={() => void openFile(node.path)}
        onContextMenu={onCtx}
        title={
          customized
            ? `${node.path}\n\nArquivo customizado — preservado em upgrades do tema padrão.`
            : node.path
        }
      >
        <span className="w-[10px]" />
        <FileIcon name={node.name} />
        <span className={'flex-1 truncate text-left ' + (tab?.loading ? 'opacity-60' : '')}>
          {node.name}
        </span>
        {customized && (
          <span
            className="h-1.5 w-1.5 rounded-full bg-amber-500"
            title="Customizado — não será sobrescrito no próximo sync"
          />
        )}
        {dirty && <span className="h-1.5 w-1.5 rounded-full bg-interactive" title="Não salvo" />}
      </button>
    );
  }

  return (
    <>
      <button
        style={pad}
        className="lc-row w-full justify-start gap-1.5"
        onClick={() => setOpen((v) => !v)}
        onContextMenu={onCtx}
      >
        <Chevron open={open} />
        <FolderIcon open={open} />
        <span className="flex-1 truncate text-left font-medium">{node.name}</span>
        <span className="text-[11px] text-text-subdued">{node.children.length}</span>
      </button>
      {open &&
        node.children.map((child) => (
          <NodeRow
            key={child.path}
            node={child}
            depth={depth + 1}
            defaultOpen={depth === 0}
            ctx={ctx}
          />
        ))}
    </>
  );
}

// ---------------------------------------------------------------------------

type MenuItemDef =
  | { type: 'item'; icon: MenuIconName; label: string; shortcut?: string; onClick: () => void; danger?: boolean; disabled?: boolean }
  | { type: 'divider' };

/**
 * Menu contextual do file tree — segue o design system Polaris do
 * editor (surface/border/text tokens). Estilo próximo ao VS Code:
 * grupos separados por divisor, atalho à direita, ícone à esquerda.
 *
 * Fecha ao clicar fora ou Escape. Auto-clamp na viewport pra não
 * cortar em borda.
 */
function ContextMenu({
  target,
  onClose,
}: {
  target: CtxTarget;
  onClose: () => void;
}) {
  const createFile = useCode((s) => s.createFile);
  const deleteFile = useCode((s) => s.deleteFile);
  const renameFile = useCode((s) => s.renameFile);
  const uploadFile = useCode((s) => s.uploadFile);
  const downloadFile = useCode((s) => s.downloadFile);
  const downloadFolder = useCode((s) => s.downloadFolder);
  const readSession = useCode((s) => s.session);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  const node = target.node;
  const isFile = !!node?.isFile;
  const isFolder = !!node && !node.isFile;
  const baseFolder = isFolder ? node!.path : (isFile ? node!.path.split('/').slice(0, -1).join('/') : '');

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  async function handleNewFile() {
    onClose();
    const name = window.prompt(
      `Novo arquivo em "${baseFolder || 'raiz'}":`,
      'meu-arquivo.liquid',
    );
    if (!name) return;
    const target = baseFolder ? `${baseFolder}/${name}` : name;
    try {
      await createFile(target);
    } catch (err: any) {
      alert(err?.message || 'Erro ao criar arquivo.');
    }
  }

  async function handleNewFolder() {
    onClose();
    /*
      S3 não tem "pasta vazia" — pastas emergem de keys que compartilham
      prefixo. Pra "criar uma pasta" a gente cria um placeholder
      `.gitkeep` dentro dela. User remove depois se quiser.
    */
    const name = window.prompt(
      `Nome da nova pasta em "${baseFolder || 'raiz'}":`,
      'minha-pasta',
    );
    if (!name) return;
    const folder = baseFolder ? `${baseFolder}/${name}` : name;
    try {
      await createFile(`${folder}/.gitkeep`, '');
    } catch (err: any) {
      alert(err?.message || 'Erro ao criar pasta.');
    }
  }

  function handleUploadClick() {
    fileInputRef.current?.click();
  }

  async function handleFilesPicked(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // reset pra permitir escolher o mesmo file de novo
    if (!files.length) return;
    onClose();
    for (const file of files) {
      try {
        await uploadFile(file, baseFolder);
      } catch (err: any) {
        alert(`Erro ao subir "${file.name}": ${err?.message || 'desconhecido'}`);
      }
    }
  }

  async function handleRename() {
    onClose();
    if (!node?.isFile) return;
    const newPath = window.prompt('Renomear arquivo:', node.path);
    if (!newPath || newPath === node.path) return;
    try {
      await renameFile(node.path, newPath);
    } catch (err: any) {
      alert(err?.message || 'Erro ao renomear.');
    }
  }

  async function handleDuplicate() {
    onClose();
    if (!node?.isFile || !readSession) return;
    const parts = node.path.split('/');
    const filename = parts.pop() || node.path;
    const dot = filename.lastIndexOf('.');
    const base = dot > 0 ? filename.slice(0, dot) : filename;
    const ext = dot > 0 ? filename.slice(dot) : '';
    const newName = window.prompt(
      'Nome da cópia:',
      `${base}-copy${ext}`,
    );
    if (!newName) return;
    const dir = parts.length ? `${parts.join('/')}/` : '';
    const target = `${dir}${newName}`;
    try {
      const { readThemeFile } = await import('../../api/client');
      const content = await readThemeFile(readSession, node.path);
      await createFile(target, content);
    } catch (err: any) {
      alert(err?.message || 'Erro ao duplicar.');
    }
  }

  async function handleDelete() {
    onClose();
    if (!node?.isFile) return;
    const ok = window.confirm(
      `Apagar "${node.path}"?\n\nSem lixeira — não dá pra desfazer.`,
    );
    if (!ok) return;
    try {
      await deleteFile(node.path);
    } catch (err: any) {
      alert(err?.message || 'Erro ao apagar.');
    }
  }

  async function handleDownloadFile() {
    onClose();
    if (!node?.isFile) return;
    try {
      await downloadFile(node.path);
    } catch (err: any) {
      alert(err?.message || 'Erro ao baixar arquivo.');
    }
  }

  async function handleDownloadFolder() {
    onClose();
    try {
      await downloadFolder(baseFolder);
    } catch (err: any) {
      alert(err?.message || 'Erro ao baixar pasta.');
    }
  }

  async function copyToClipboard(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      // Sem toast global aqui, mas o menu já fechou — feedback via title
      // do próximo focus se quiser. Não avisamos pra não interromper.
    } catch {
      // Fallback pré-clipboard-api
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch {}
      ta.remove();
    }
    // eslint-disable-next-line no-console
    console.log(`[file-tree] ${label} copiado:`, text);
  }

  function handleCopyPath() {
    onClose();
    if (!node) return;
    void copyToClipboard(node.path, 'Caminho');
  }

  function handleCopyRelativePath() {
    onClose();
    if (!node) return;
    // "Relativo" ao TEMA — nossa raiz. Já é relativo por default no engine,
    // mas mantemos como ação separada pra alinhar com VS Code UX. Se
    // adicionarmos prefixo absoluto no futuro (`{tenant}/Themes/...`),
    // essa distinção passa a valer.
    const parts = node.path.split('/');
    const relativeToParent = parts.length > 1 ? parts.slice(-1)[0] : node.path;
    void copyToClipboard(relativeToParent, 'Caminho relativo');
  }

  function handleOpen() {
    onClose();
    if (!node?.isFile) return;
    void useCode.getState().openFile(node.path);
  }

  /*
    Menu items — dinâmicos por contexto (arquivo vs pasta/root),
    agrupados por seção (grupo separado por divider), exatamente como
    o VS Code faz.
  */
  const items: MenuItemDef[] = isFile
    ? [
        { type: 'item', icon: 'external-link', label: 'Abrir', onClick: handleOpen },
        { type: 'divider' },
        { type: 'item', icon: 'edit', label: 'Renomear', shortcut: 'F2', onClick: handleRename },
        { type: 'item', icon: 'copy', label: 'Duplicar', onClick: handleDuplicate },
        { type: 'divider' },
        { type: 'item', icon: 'clipboard', label: 'Copiar caminho', onClick: handleCopyPath },
        { type: 'item', icon: 'clipboard', label: 'Copiar nome do arquivo', onClick: handleCopyRelativePath },
        { type: 'divider' },
        { type: 'item', icon: 'download', label: 'Download', onClick: handleDownloadFile },
        { type: 'divider' },
        { type: 'item', icon: 'trash', label: 'Excluir', shortcut: 'Del', onClick: handleDelete, danger: true },
      ]
    : [
        { type: 'item', icon: 'file-plus', label: 'Novo arquivo', onClick: handleNewFile },
        { type: 'item', icon: 'folder-plus', label: 'Nova pasta', onClick: handleNewFolder },
        { type: 'divider' },
        { type: 'item', icon: 'upload', label: 'Upload…', onClick: handleUploadClick },
        { type: 'item', icon: 'download', label: baseFolder ? 'Baixar pasta (ZIP)' : 'Baixar tema (ZIP)', onClick: handleDownloadFolder },
        { type: 'divider' },
        { type: 'item', icon: 'clipboard', label: 'Copiar caminho', onClick: handleCopyPath, disabled: !node },
      ];

  /*
    Clamp position within viewport — se clicou perto da borda direita/
    inferior, joga o menu pra dentro pra não cortar.
  */
  const MENU_W = 232;
  const itemCount = items.filter((it) => it.type === 'item').length;
  const dividerCount = items.filter((it) => it.type === 'divider').length;
  const MENU_H = itemCount * 30 + dividerCount * 9 + 8;
  const left = Math.min(target.x, window.innerWidth - MENU_W - 8);
  const top = Math.min(target.y, window.innerHeight - MENU_H - 8);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFilesPicked}
        className="hidden"
      />
      <div
        ref={ref}
        role="menu"
        className="fixed z-[100] w-[232px] overflow-hidden rounded-lg border border-border bg-surface py-1 text-[13px] text-text shadow-[0_12px_32px_rgba(0,0,0,0.18)]"
        style={{ left, top }}
      >
        {items.map((it, idx) => {
          if (it.type === 'divider') {
            return <div key={`div-${idx}`} className="my-1 h-px bg-border-subdued" />;
          }
          return (
            <MenuItem
              key={`it-${idx}`}
              icon={it.icon}
              label={it.label}
              shortcut={it.shortcut}
              danger={it.danger}
              disabled={it.disabled}
              onClick={it.onClick}
            />
          );
        })}
      </div>
    </>
  );
}

function MenuItem({
  icon,
  label,
  shortcut,
  onClick,
  danger,
  disabled,
}: {
  icon: MenuIconName;
  label: string;
  shortcut?: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={
        'flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors ' +
        (disabled
          ? 'cursor-not-allowed text-text-disabled'
          : danger
          ? 'text-critical hover:bg-surface-hovered'
          : 'text-text hover:bg-surface-hovered')
      }
    >
      <span className={danger ? 'text-critical' : 'text-icon-subdued'}>
        <MenuIcon name={icon} />
      </span>
      <span className="flex-1 truncate">{label}</span>
      {shortcut && (
        <span className="shrink-0 text-[11px] tracking-wide text-text-subdued">
          {shortcut}
        </span>
      )}
    </button>
  );
}

type MenuIconName =
  | 'edit'
  | 'copy'
  | 'trash'
  | 'file-plus'
  | 'folder-plus'
  | 'upload'
  | 'download'
  | 'clipboard'
  | 'external-link';

function MenuIcon({ name }: { name: MenuIconName }) {
  const p = {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
    case 'edit':
      return (
        <svg {...p}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
      );
    case 'copy':
      return (
        <svg {...p}>
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      );
    case 'trash':
      return (
        <svg {...p}>
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
          <path d="M10 11v6M14 11v6" />
          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
        </svg>
      );
    case 'file-plus':
      return (
        <svg {...p}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="12" y1="18" x2="12" y2="12" />
          <line x1="9" y1="15" x2="15" y2="15" />
        </svg>
      );
    case 'folder-plus':
      return (
        <svg {...p}>
          <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2z" />
          <line x1="12" y1="11" x2="12" y2="17" />
          <line x1="9" y1="14" x2="15" y2="14" />
        </svg>
      );
    case 'upload':
      return (
        <svg {...p}>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      );
    case 'download':
      return (
        <svg {...p}>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      );
    case 'clipboard':
      return (
        <svg {...p}>
          <rect x="8" y="2" width="8" height="4" rx="1" />
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
        </svg>
      );
    case 'external-link':
      return (
        <svg {...p}>
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------

export function FileTree() {
  const files = useCode((s) => s.files);
  const loading = useCode((s) => s.filesLoading);
  const error = useCode((s) => s.filesError);
  const refresh = useCode((s) => s.refreshFiles);
  const [filter, setFilter] = useState('');
  const [ctx, setCtx] = useState<CtxTarget | null>(null);
  const ctxSetter: CtxSetter = { open: setCtx };

  const tree = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = q ? files.filter((f) => f.toLowerCase().includes(q)) : files;
    return buildTree(list);
  }, [files, filter]);

  return (
    <aside className="flex h-full w-[260px] shrink-0 flex-col border-r border-border bg-surface">
      <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-subdued">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          </svg>
          <span>Arquivos</span>
        </div>
        <button
          onClick={() => void refresh()}
          className="lc-btn-ghost flex h-6 w-6 items-center justify-center"
          title="Recarregar arquivos"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M3 12a9 9 0 0 1 15.65-6.12M21 3v6h-6" />
            <path d="M21 12a9 9 0 0 1-15.65 6.12M3 21v-6h6" />
          </svg>
        </button>
      </div>

      <div className="border-b border-border-subdued px-2 py-2">
        <input
          className="lc-input"
          placeholder="Filtrar arquivos…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ padding: '4px 8px', fontSize: 12 }}
        />
      </div>

      {/*
        onContextMenu no container inteiro pra que clique-direito no
        vazio (fundo abaixo dos arquivos) abra o menu com ações "sem
        alvo" (Novo arquivo/Nova pasta/Upload/Download no root do tema).
      */}
      <div
        className="lc-scroll flex-1 overflow-y-auto p-1 text-[13px]"
        onContextMenu={(e) => {
          if (e.target === e.currentTarget) {
            e.preventDefault();
            setCtx({ x: e.clientX, y: e.clientY, node: null });
          }
        }}
      >
        {loading && (
          <div className="flex items-center gap-2 px-3 py-4 text-xs text-text-subdued">
            <div className="lc-spin h-3 w-3 rounded-full border border-border border-t-interactive" />
            Carregando arquivos…
          </div>
        )}
        {error && <p className="p-3 text-xs text-critical">{error}</p>}
        {!loading && !error && tree.children.length === 0 && (
          <p className="p-3 text-xs text-text-subdued">Nenhum arquivo encontrado.</p>
        )}
        {tree.children.map((child) => (
          <NodeRow
            key={child.path}
            node={child}
            depth={0}
            defaultOpen={true}
            ctx={ctxSetter}
          />
        ))}
      </div>
      {ctx && <ContextMenu target={ctx} onClose={() => setCtx(null)} />}
    </aside>
  );
}
